import { didShimSelf } from "./server-shim.js";
import { ShakaLiveMixin } from "./live.js";
import { ShakaMediaTracksMixin } from "./media-tracks.js";
import { ShakaStreamTypeMixin } from "./stream-type.js";
import { MediaError, MediaStreamTypes } from "@videojs/media";
import { HTMLVideoAdapter } from "@videojs/media/dom";
import { MediaTracksMixin } from "@videojs/media/media-tracks";
import { deepEqual } from "@videojs/utils/object";
import { isObject } from "@videojs/utils/predicate";
import shaka from "shaka-player/dist/shaka-player.compiled-es2021.js";
//#region src/adapter.ts
if (didShimSelf) Reflect.deleteProperty(globalThis, "self");
/**
* Configuration this element applies on top of Shaka's own defaults — the Shaka spelling of what the hls.js media ships
* (`capLevelToPlayerSize`): adaptation stays within renditions no larger than the element rendering them. Anything
* under `source.engine.shaka` overrides it.
*/
const defaultShakaConfig = { abr: { restrictToElementSize: true } };
var ShakaAdapterCore = class ShakaAdapterCore extends MediaTracksMixin(HTMLVideoAdapter) {
	static {
		this.defaultProps = {
			src: "",
			source: null,
			preload: "metadata",
			streamType: MediaStreamTypes.UNKNOWN
		};
	}
	#engine = null;
	#src = ShakaAdapterCore.defaultProps.src;
	#source = ShakaAdapterCore.defaultProps.source;
	#preload = ShakaAdapterCore.defaultProps.preload;
	#playIntentAbort = null;
	#isLoadDeferred = false;
	#clampedGoals = null;
	#error = null;
	#reported = /* @__PURE__ */ new WeakSet();
	#isDestroyed = false;
	constructor() {
		super();
		if (typeof document === "undefined") return;
		installPolyfills();
		if (!shaka.Player.isBrowserSupported()) return;
		this.#engine = new shaka.Player();
		this.#engine.configure(defaultShakaConfig);
		this.#engine.addEventListener("error", this.#onEngineError);
	}
	attach(target) {
		const isNewTarget = this.target !== target;
		const hasUnloadedSource = Boolean(this.#src);
		super.attach(target);
		if (target.preload !== this.#preload) target.preload = this.#preload;
		const engine = this.#engine;
		if (!engine || !isNewTarget) return;
		this.#run(engine.attach(target));
		if (hasUnloadedSource) this.#loadSource(engine);
	}
	detach() {
		const engine = this.#engine;
		const wasAttached = this.target !== null;
		this.#disarmPlayIntent();
		super.detach();
		if (engine && wasAttached) this.#run(engine.detach());
	}
	destroy() {
		this.detach();
		this.#disarmPlayIntent();
		const engine = this.#engine;
		this.#engine = null;
		this.#isDestroyed = true;
		if (engine) {
			engine.removeEventListener("error", this.#onEngineError);
			engine.destroy().catch(() => {});
		}
		super.destroy();
	}
	/**
	* Underlying playback engine — the Shaka `Player` instance, or `null` when the browser cannot run it. An advanced
	* escape hatch for direct engine access; normal playback is driven through this element's own properties and
	* methods.
	*/
	get engine() {
		return this.#engine;
	}
	/**
	* The last playback failure Shaka could not recover from, or `null`. Shaka loads asynchronously, so a manifest that
	* cannot be played fails after `src` was assigned rather than at the assignment; the `error` event is when to read
	* this.
	*
	* A Shaka failure says more about what went wrong than the media element's own `error` does, so it wins while there
	* is one; anything the element failed on by itself still reads through.
	*/
	get error() {
		return this.#error ?? super.error;
	}
	get src() {
		return this.#src;
	}
	/** Manifest URL. Setting it re-derives `source`, carrying its settings over. */
	set src(value) {
		const { type, drm, engine } = this.#source ?? {};
		const next = {
			...type && { type },
			...drm && { drm },
			...engine && { engine },
			...value && { src: value }
		};
		this.source = Object.keys(next).length > 0 ? next : null;
	}
	/**
	* How much to fetch before playback is asked for, mirroring the media element's own `preload`. Shaka has no split
	* between "know the source" and "buffer it", so this maps onto when and how `engine.load()` runs:
	*
	* - `'auto'` — load immediately with the configured buffering goals.
	* - `'metadata'` — load immediately, holding buffering to about a segment; the configured goals come back on the first
	*   play intent.
	* - `'none'` / `''` — hold the load entirely until the first play intent.
	*
	* A play intent is a `play` event, a target that is already playing, or `autoplay` — which the spec lets override
	* `preload` for good reason: with nothing fetched there is nothing whose readiness could ever fire it. Widening
	* (`none` → `auto`) takes effect immediately; narrowing after a load began cannot un-fetch and leaves it alone.
	*/
	get preload() {
		return this.#preload;
	}
	set preload(value) {
		if (this.#preload === value) return;
		this.#preload = value;
		const { target } = this;
		if (target && target.preload !== value) target.preload = value;
		const engine = this.#engine;
		if (!engine) return;
		if (this.#isLoadDeferred && (value === "metadata" || value === "auto")) {
			this.#loadSource(engine);
			return;
		}
		if (this.#clampedGoals && value === "auto") this.#restoreBuffering(engine);
	}
	/**
	* Structured source: what to play (`src`, an optional `type`) plus how to play it (`drm`, `engine.shaka`). Replacing
	* it re-derives `src`.
	*
	* Shaka takes configuration on a live player, so changing `engine.shaka` re-applies it in place instead of recreating
	* the engine.
	*/
	get source() {
		return this.#source;
	}
	set source(value) {
		const source = value ?? null;
		if (source === this.#source) return;
		const configChanged = !deepEqual(engineConfigKey(this.#source), engineConfigKey(source));
		const loadChanged = this.#src !== (source?.src ?? "") || this.#source?.type !== source?.type;
		this.#source = source;
		this.#src = source?.src ?? "";
		if (configChanged) this.#applyEngineConfig();
		if (loadChanged) this.#requestLoad();
		this.dispatchEvent(new Event("sourcechange"));
	}
	#applyEngineConfig() {
		const engine = this.#engine;
		if (!engine) return;
		engine.resetConfiguration();
		engine.configure(defaultShakaConfig);
		const config = withDrmConfig(this.#source?.engine?.shaka, this.#source?.drm);
		if (config) engine.configure(config);
		if (this.#clampedGoals) this.#clampBuffering(engine);
	}
	#requestLoad() {
		const engine = this.#engine;
		if (!engine || !this.target) return;
		this.#loadSource(engine);
	}
	#loadSource(engine) {
		this.#error = null;
		this.#disarmPlayIntent();
		this.#isLoadDeferred = false;
		this.#restoreBuffering(engine);
		const { src } = this;
		if (!src) {
			this.#run(engine.unload());
			return;
		}
		const { target } = this;
		const hasPlayIntent = Boolean(target && (!target.paused || target.autoplay));
		const preload = this.#preload;
		if (!hasPlayIntent && (preload === "none" || preload === "")) {
			this.#isLoadDeferred = true;
			this.#armPlayIntent(() => {
				const deferredEngine = this.#engine;
				if (deferredEngine) this.#startLoad(deferredEngine);
			});
			return;
		}
		if (!hasPlayIntent && preload === "metadata") {
			this.#clampBuffering(engine);
			this.#armPlayIntent(() => {
				const clampedEngine = this.#engine;
				if (clampedEngine) this.#restoreBuffering(clampedEngine);
			});
		}
		this.#startLoad(engine);
	}
	#startLoad(engine) {
		this.#isLoadDeferred = false;
		this.#error = null;
		this.#run(engine.load(this.src, void 0, this.#source?.type));
	}
	/**
	* Holds Shaka's buffering to about one segment — the closest analog of fetching "metadata" an engine that always
	* buffers through MSE has. The goals in force are captured first so the first play intent can put back exactly what
	* configuration produced them.
	*/
	#clampBuffering(engine) {
		const { bufferingGoal, rebufferingGoal } = engine.getConfiguration().streaming;
		this.#clampedGoals = {
			bufferingGoal,
			rebufferingGoal
		};
		engine.configure({ streaming: {
			bufferingGoal: 1,
			rebufferingGoal: 1
		} });
	}
	#restoreBuffering(engine) {
		const goals = this.#clampedGoals;
		if (!goals) return;
		this.#clampedGoals = null;
		engine.configure({ streaming: goals });
	}
	#armPlayIntent(onPlay) {
		this.#disarmPlayIntent();
		const { target } = this;
		if (!target) return;
		this.#playIntentAbort = new AbortController();
		target.addEventListener("play", onPlay, {
			signal: this.#playIntentAbort.signal,
			once: true
		});
	}
	#disarmPlayIntent() {
		this.#playIntentAbort?.abort();
		this.#playIntentAbort = null;
	}
	/**
	* Shaka's `attach()`, `load()`, `unload()`, and `detach()` are asynchronous, and the player orders them itself: each
	* takes an internal lock in call order, and a newer one interrupts whatever it supersedes. So a call is issued as it
	* comes — holding it until the one before it settles would keep a new source waiting on a manifest that hangs, when
	* cutting that load short is the point of assigning one.
	*
	* Nothing awaits these, so a rejection is reported rather than left unhandled.
	*/
	#run(operation) {
		operation.catch((error) => this.#reportError(error));
	}
	#onEngineError = (event) => {
		this.#reportError(event.detail);
	};
	#reportError(error) {
		if (this.#isDestroyed) return;
		if (isObject(error)) {
			if (this.#reported.has(error)) return;
			this.#reported.add(error);
		}
		const mediaError = toMediaError(error);
		if (!mediaError) return;
		this.#error = mediaError;
		this.dispatchEvent(new ErrorEvent("error", {
			error: mediaError,
			message: mediaError.message
		}));
	}
};
/**
* @fires sourcechange - Fired when `source` changes, either directly or by resolving a new `src`. Read `source` for the
*   new value.
* @fires error - Fired when playback fails in a way Shaka could not recover from. Read `error` for the failure.
* @fires streamtypechange - Fired when the detected stream type changes. Read `streamType` for the new value.
* @fires targetlivewindowchange - Fired when `targetLiveWindow` changes. Read it for the new value.
*/
var ShakaAdapter = class extends ShakaLiveMixin(ShakaStreamTypeMixin(ShakaMediaTracksMixin(ShakaAdapterCore))) {};
let arePolyfillsInstalled = false;
/**
* Patches the browser APIs Shaka expects before anything reads them. Shaka ships these as polyfills it does not install
* itself, and both `isBrowserSupported()` and the DRM path depend on them — FairPlay EME on Safari is patched in from
* here. Installing is global, so it happens once.
*/
function installPolyfills() {
	if (arePolyfillsInstalled) return;
	arePolyfillsInstalled = true;
	shaka.polyfill.installAll();
}
/**
* Everything Shaka is configured from. Compared structurally, so equivalent options never re-apply — including nested
* ones like `drm`, which a flat comparison would see as changed whenever the object identity did.
*/
function engineConfigKey(source) {
	return {
		drm: source?.drm ?? null,
		shaka: source?.engine?.shaka ?? null
	};
}
/**
* Shaka configuration with the source's DRM licensing folded in. Shaka names license servers under `drm.servers` and
* server certificates under `drm.advanced`, so `source.drm` is translated into both — unless `engine.shaka.drm.servers`
* names servers of its own, which replaces it.
*/
function withDrmConfig(config, drm) {
	const systems = Object.entries(drm ?? {});
	if (systems.length === 0 || config?.drm?.servers) return config;
	const servers = {};
	const advanced = {};
	for (const [keySystem, system] of systems) {
		servers[keySystem] = system.licenseUrl;
		if (system.serverCertificateUrl) advanced[keySystem] = { serverCertificateUri: system.serverCertificateUrl };
	}
	return {
		...config,
		drm: {
			...config?.drm,
			servers,
			...Object.keys(advanced).length > 0 && { advanced: {
				...advanced,
				...config?.drm?.advanced
			} }
		}
	};
}
const categoryToCode = {
	[shaka.util.Error.Category.NETWORK]: MediaError.MEDIA_ERR_NETWORK,
	[shaka.util.Error.Category.MEDIA]: MediaError.MEDIA_ERR_DECODE,
	[shaka.util.Error.Category.MANIFEST]: MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED,
	[shaka.util.Error.Category.STREAMING]: MediaError.MEDIA_ERR_DECODE,
	[shaka.util.Error.Category.DRM]: MediaError.MEDIA_ERR_ENCRYPTED
};
/** Shaka codes for a load that a newer one replaced. */
const abortedCodes = /* @__PURE__ */ new Set([shaka.util.Error.Code.LOAD_INTERRUPTED, shaka.util.Error.Code.OPERATION_ABORTED]);
/**
* A Shaka failure as a `MediaError`, or `null` when there is nothing to report.
*
* Only a failure Shaka gave up on is reported. Shaka marks one it means to retry as recoverable and fires it on the way
* through, and an announced error stands until the next load — so announcing those would leave the error UI over
* playback that is still running. Whatever a retry cannot save comes back as critical.
*
* Shaka classifies a failure by category rather than by a media error code, so the code is derived from that.
*/
function toMediaError(error) {
	if (!isShakaError(error)) return error instanceof Error ? new MediaError(error.message, MediaError.MEDIA_ERR_CUSTOM, true) : null;
	if (abortedCodes.has(error.code)) return null;
	if (error.severity !== shaka.util.Error.Severity.CRITICAL) return null;
	const code = categoryToCode[error.category] ?? MediaError.MEDIA_ERR_CUSTOM;
	const mediaError = new MediaError(error.message, code, true, `shaka-${error.code}`);
	mediaError.data = error;
	return mediaError;
}
function isShakaError(value) {
	return typeof value === "object" && value !== null && "code" in value && "category" in value;
}
//#endregion
export { ShakaAdapter, shaka };
